import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState, useEffect, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import { MobileFilterDisclosure } from '~/components/data/MobileFilterDisclosure'
import {
  ArchiveTeamDialog,
  ChangeTeamTypeDialog,
  DeleteTeamDialog,
  FlagTeamDialog,
  TeamsTable,
  teamsKeys,
  useArchiveTeamMutation,
  useFlagTeamMutation,
  useRestoreTeamMutation,
  useSetTeamTypeMutation,
  useSoftDeleteTeamMutation,
  type TeamRow,
} from '~/features/teams'
import type { TeamRowAction } from '~/features/teams/components/TeamsTable'
import { http } from '~/lib/http'
import { useDebouncedValue } from '~/lib/query/use-debounced-value'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'
import type { AppError } from '~/lib/result'

const teamsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  search: z.string().optional(),
  archived: z.enum(['all', 'active', 'archived']).default('active'),
  view: z.enum(['all', 'flagged', 'no-subscription']).default('all'),
  type: z.enum(['default', 'missionary', 'ngo', 'organization']).optional(),
})
type TeamsSearch = z.infer<typeof teamsSearchSchema>

const URL_DEFAULTS: TeamsSearch = {
  page: 1,
  limit: 25,
  archived: 'active',
  view: 'all',
}

/**
 * `/admin/teams` — list route (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `search`, `archived`.
 */
export const Route = createFileRoute('/admin/teams/')({
  validateSearch: teamsSearchSchema,
  component: TeamsListPage,
})

interface TeamsListPayload {
  readonly teams?: readonly TeamRow[]
  readonly items?: readonly TeamRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchTeamsPage(
  params: PaginatedParams,
): Promise<PaginatedResult<TeamRow>> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
    offset: (params.page - 1) * params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sort) query.sort = params.sort
  const archived = params.filters?.archived
  if (archived) query.archived = archived
  const view = params.filters?.view
  if (view) query.view = view
  const type = params.filters?.type
  if (type) query.type = type

  const raw = await http.get<TeamsListPayload>('/api/v1/admin/teams', {
    query,
  })
  const rows = (raw.teams ?? raw.items ?? []) as readonly TeamRow[]
  const total = raw.pagination?.total ?? raw.total ?? rows.length
  return toPaginatedResult(rows, total, params, raw.pagination)
}

function TeamsListPage(): ReactElement {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [, setUrl] = useUrlState(teamsSearchSchema, URL_DEFAULTS)

  const [searchInput, setSearchInput] = useState<string>(search.search ?? '')
  const debouncedSearch = useDebouncedValue(searchInput, 300)
  useEffect(() => {
    const normalized = debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim()
    if (normalized !== search.search) {
      setUrl({ search: normalized, page: 1 })
    }
  }, [debouncedSearch, search.search, setUrl])

  // Active dialog target. Only one dialog is ever open — the action verb
  // selects which dialog renders. Keep `team` separately so unmount still
  // animates with the team name visible.
  const [dialog, setDialog] = useState<{
    readonly action: TeamRowAction
    readonly team: TeamRow
  } | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<{
    readonly id: string
    readonly name: string
  } | null>(null)

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    search: search.search,
    filters: { archived: search.archived, view: search.view, type: search.type },
  }

  const listQuery = usePaginatedQuery<TeamRow>({
    queryKey: [...teamsKeys.all, 'list-paginated'],
    params,
    fetcher: fetchTeamsPage,
  })

  const teams = useMemo(() => listQuery.data?.data ?? [], [listQuery.data])
  const pagination = listQuery.data?.pagination
  const total = pagination?.total ?? 0

  const teamsById = useMemo(
    () => new Map(teams.map((t) => [t.id, t])),
    [teams],
  )

  const archiveMutation = useArchiveTeamMutation(archiveTarget?.id ?? '')
  const restoreMutation = useRestoreTeamMutation('')

  // Single-row dialog mutations. We instantiate them with the active
  // dialog's team id so each mutation invalidates the right detail cache
  // on success. Empty string when no dialog is open is harmless — the
  // mutation only fires on user submit.
  const dialogTeamId = dialog?.team.id ?? ''
  const flagMutation = useFlagTeamMutation(dialogTeamId)
  const setTypeMutation = useSetTeamTypeMutation(dialogTeamId)
  const deleteMutation = useSoftDeleteTeamMutation(dialogTeamId)

  const apiError = listQuery.error as AppError | null
  const isPermissionDenied =
    apiError?.code === 'forbidden' || apiError?.code === 'unauthorized'

  function handleArchiveClick(ids: readonly string[]): void {
    if (ids.length === 0) return
    const id = ids[0]
    if (id === undefined) return
    const team = teamsById.get(id)
    if (team === undefined) return
    setArchiveTarget({ id: team.id, name: team.name })
  }

  async function handleArchiveSubmit(input: {
    readonly reason: string | undefined
  }): Promise<void> {
    if (archiveTarget === null) return
    await archiveMutation.mutateAsync(input)
    setArchiveTarget(null)
  }

  function handleRestoreClick(ids: readonly string[]): void {
    if (ids.length === 0) return
    const id = ids[0]
    if (id === undefined) return
    void restoreMutation.mutateAsync()
  }

  function handleRowAction(action: TeamRowAction, team: TeamRow): void {
    if (action === 'view') {
      void navigate({
        to: '/admin/teams/$teamId',
        params: { teamId: team.id },
      })
      return
    }
    if (action === 'archive') {
      // Reuse the existing archive flow + dialog so the typed confirm
      // and bulk-archive surface stay in sync.
      setArchiveTarget({ id: team.id, name: team.name })
      return
    }
    setDialog({ action, team })
  }

  function closeDialog(): void {
    const pending =
      flagMutation.isPending ||
      setTypeMutation.isPending ||
      deleteMutation.isPending
    if (pending) return
    setDialog(null)
    flagMutation.reset()
    setTypeMutation.reset()
    deleteMutation.reset()
  }

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Teams</h1>
          <p className="ka-page-header-subtitle">
            {listQuery.isPending
              ? 'Loading…'
              : `${total} total · filter: ${search.archived}`}
          </p>
        </div>
      </header>

      <div className="ka-filter-strip" role="region" aria-label="Filters">
        <MobileFilterDisclosure
          id="team-list-filters"
          activeCount={search.archived === 'all' ? 0 : 1}
        >
          <div className="ka-filter-group">
            <FilterChip
              label="Active"
              active={search.archived === 'active' && search.view === 'all'}
              onClick={() => setUrl({ archived: 'active', view: 'all', page: 1 })}
            />
            <FilterChip
              label="Archived"
              active={search.archived === 'archived' && search.view === 'all'}
              onClick={() => setUrl({ archived: 'archived', view: 'all', page: 1 })}
            />
            <FilterChip
              label="All"
              active={search.archived === 'all' && search.view === 'all'}
              onClick={() => setUrl({ archived: 'all', view: 'all', page: 1 })}
            />
            <FilterChip
              label="Flagged"
              active={search.view === 'flagged'}
              onClick={() => setUrl({ archived: 'active', view: 'flagged', page: 1 })}
            />
            <FilterChip
              label="No subscription"
              active={search.view === 'no-subscription'}
              onClick={() =>
                setUrl({ archived: 'active', view: 'no-subscription', page: 1 })
              }
            />
          </div>
        </MobileFilterDisclosure>
        <div className="ka-filter-search">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            placeholder="Search teams…"
            aria-label="Search teams"
            className="ka-field-input"
          />
        </div>
      </div>

      {isPermissionDenied ? (
        <div role="alert" className="ka-card ka-empty">
          <div className="ka-empty-title">You don&apos;t have access</div>
          <p className="ka-empty-body">
            Team admin requires platform-admin permission. If you should
            have access, ask a super-admin to grant the role.
          </p>
        </div>
      ) : (
        <>
          <TeamsTable
            teams={teams}
            total={total}
            isLoading={listQuery.isPending}
            isError={listQuery.isError}
            errorMessage={
              listQuery.error instanceof Error
                ? listQuery.error.message
                : undefined
            }
            canManage
            onArchive={handleArchiveClick}
            onRestore={handleRestoreClick}
            onRowAction={handleRowAction}
          />
          <DataTablePagination
            page={pagination?.page ?? search.page}
            limit={pagination?.limit ?? search.limit}
            total={total}
            isLoading={listQuery.isPending}
            onChange={({ page, limit }) => setUrl({ page, limit })}
          />
        </>
      )}

      {archiveTarget !== null ? (
        <ArchiveTeamDialog
          teamName={archiveTarget.name}
          onClose={() =>
            archiveMutation.isPending ? undefined : setArchiveTarget(null)
          }
          onSubmit={handleArchiveSubmit}
          isSubmitting={archiveMutation.isPending}
          errorMessage={
            archiveMutation.error instanceof Error
              ? archiveMutation.error.message
              : undefined
          }
        />
      ) : null}

      {dialog?.action === 'change-type' ? (
        <ChangeTeamTypeDialog
          teamName={dialog.team.name}
          currentType={dialog.team.team_type}
          isSubmitting={setTypeMutation.isPending}
          errorMessage={
            setTypeMutation.error instanceof Error
              ? setTypeMutation.error.message
              : undefined
          }
          onClose={closeDialog}
          onSubmit={async (body) => {
            await setTypeMutation.mutateAsync(body)
            setDialog(null)
          }}
        />
      ) : null}

      {dialog?.action === 'flag' ? (
        <FlagTeamDialog
          teamName={dialog.team.name}
          /* `archived_at` is on TeamRow; the "is flagged" state isn't
             exposed in the list payload yet, so for now treat every flag
             dialog open as "set flag" — backend audit is the truth. */
          isFlagged={false}
          isSubmitting={flagMutation.isPending}
          errorMessage={
            flagMutation.error instanceof Error
              ? flagMutation.error.message
              : undefined
          }
          onClose={closeDialog}
          onSubmit={async (body) => {
            await flagMutation.mutateAsync(body)
            setDialog(null)
          }}
        />
      ) : null}

      {dialog?.action === 'delete' ? (
        <DeleteTeamDialog
          teamName={dialog.team.name}
          isSubmitting={deleteMutation.isPending}
          errorMessage={
            deleteMutation.error instanceof Error
              ? deleteMutation.error.message
              : undefined
          }
          onClose={closeDialog}
          onSubmit={async (body) => {
            await deleteMutation.mutateAsync(body)
            setDialog(null)
          }}
        />
      ) : null}

      {dialog?.action === 'restore' ? (
        <RestoreInline
          teamName={dialog.team.name}
          teamId={dialog.team.id}
          onClose={closeDialog}
          onDone={() => setDialog(null)}
        />
      ) : null}
    </div>
  )
}

interface RestoreInlineProps {
  readonly teamName: string
  readonly teamId: string
  readonly onClose: () => void
  readonly onDone: () => void
}

/**
 * Tiny inline "Restore?" confirm — restore is non-destructive enough
 * that a dedicated reason-required modal would be overkill. Kept
 * co-located with the route so it doesn't bloat the feature barrel.
 */
function RestoreInline({
  teamName,
  teamId,
  onClose,
  onDone,
}: RestoreInlineProps): ReactElement {
  const restore = useRestoreTeamMutation(teamId)
  return (
    <div
      role="presentation"
      className="ka-modal-backdrop ka-modal-backdrop--blur"
      onClick={(event) => {
        if (event.target === event.currentTarget && !restore.isPending) onClose()
      }}
    >
      <div role="dialog" aria-modal="true" className="ka-modal">
        <header className="ka-modal-head ka-modal-head--padded">
          <div className="ka-modal-title-wrap">
            <h2 className="ka-modal-head-title">Restore {teamName}?</h2>
          </div>
        </header>
        <div className="ka-modal-body ka-modal-body--plain">
          <p className="ka-modal-desc">
            Restored teams reappear in the customer dashboard immediately.
            Members keep their roles.
          </p>
          {restore.error instanceof Error ? (
            <div role="alert" className="ka-error-banner">
              {restore.error.message}
            </div>
          ) : null}
        </div>
        <footer className="ka-modal-foot ka-modal-foot--tinted">
          <button
            type="button"
            className="ka-btn ka-btn--ghost"
            onClick={onClose}
            disabled={restore.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            disabled={restore.isPending}
            onClick={() => {
              void restore.mutateAsync().then(() => onDone())
            }}
          >
            {restore.isPending ? 'Restoring…' : 'Restore'}
          </button>
        </footer>
      </div>
    </div>
  )
}

interface FilterChipProps {
  readonly label: string
  readonly active: boolean
  readonly onClick: () => void
}

function FilterChip({ label, active, onClick }: FilterChipProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`ka-detail-chip${active ? ' is-active' : ''}`}
    >
      {label}
    </button>
  )
}
